import React from 'react';
import {Link} from 'react-router-dom';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {Pill} from 'src/design/atoms/Pill';
import {Text} from 'src/design/atoms/Text';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {useGetDemonstrationContextQuery} from 'src/redux/queries/courseSession';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {CourseSessionPillConfig} from './coursePills';

/**
 * @registry Info banner naming the trainee, course, and session status for a demonstration task form.
 */
export interface DemonstrationContextBannerProps {
  taskAssignmentId?: number;
}

export const DemonstrationContextBanner: React.FC<
  DemonstrationContextBannerProps
> = ({taskAssignmentId}) => {
  const {data, isLoading, isError} = useGetDemonstrationContextQuery(
    {taskAssignmentId: taskAssignmentId ?? 0},
    {skip: !taskAssignmentId, refetchOnMountOrArgChange: true}
  );

  if (isLoading) {
    return (
      <Center py="xs">
        <Loader size="xs" />
      </Center>
    );
  }

  if (isError || !data) return null;

  const details = [data.courseName, data.demonstrationTitle].filter(
    (part): part is string => !!part
  );

  return (
    <InfoAlert title="Training demonstration" mb="sm">
      <Group gap="xs" wrap="wrap" align="center">
        {CourseSessionPillConfig(data.sessionState).map(pill => (
          <Pill key={pill.text} text={pill.text} color={pill.color} />
        ))}
        <Text size="sm">
          For{' '}
          <Text span fw={600} inherit>
            {data.traineeName}
          </Text>
          {details.length > 0 && ` · ${details.join(' · ')}`}
        </Text>
        <PermissionGuard
          module={ModuleEnum.Training}
          operation={PermissionOperationEnum.View}>
          <NavAnchor
            component={Link}
            text="View session"
            to={PathUtility.TrainingCourseSessionView(
              String(data.courseDefinitionId),
              String(data.courseAssignmentId),
              String(data.sessionId),
              'overview'
            )}
          />
        </PermissionGuard>
      </Group>
    </InfoAlert>
  );
};
